import { useMemo } from "react";
import { JointOfferResponse, OfferResponse, OrderResponse } from "../../types";

/** Pure — one seller's `OfferResponse[]` per key collapses into a single flat list. Split out so
 * it's independently unit-testable (`useJointBidOffers.test.ts`) without rendering the hook. */
export const flattenOffers = (
  multipleOffers: OfferResponse[][]
): OfferResponse[] => multipleOffers.flat();

/** Pure — how many suppliers offered: one who quoted several lines is still one (SCRUM-60). */
export const countOfferingSuppliers = (offers: OfferResponse[]): number =>
  new Set(offers.map((offer) => offer.user?.id)).size;

/** Pure — maps each joint order line's material id to its display name, preferring the aligned
 * offer's own material (an offer's `order` can be a stale/duplicated copy of the order line) and
 * falling back to `jointOrder[idx]` when no offer landed on that line yet. */
export const mapMaterials = (
  alignedOffers: Array<OfferResponse | null>,
  jointOrder: OrderResponse[]
): Record<string, string> =>
  alignedOffers.reduce((acc: Record<string, string>, offer, idx) => {
    if (offer) {
      const { id, globalMaterialName } = offer.order.material;
      acc[id] = globalMaterialName;
    } else {
      const { id, globalMaterialName } = jointOrder[idx].material;
      acc[id] = globalMaterialName;
    }
    return acc;
  }, {});

/**
 * Pure — the joint line an offer belongs to: its own order line, or, for an offer whose order is
 * not one of the lines, the line of its material when that material is on a single line (on
 * several lines the material alone cannot say which). -1 when it belongs to none.
 */
export const lineIndexOf = (
  lines: OrderResponse[],
  offer: OfferResponse
): number => {
  const byLine = lines.findIndex((line) => line.id === offer.order?.id);
  if (byLine >= 0) return byLine;
  const materialId = offer.order?.material?.id;
  const sameMaterial = lines.filter((line) => line.material?.id === materialId);
  return sameMaterial.length === 1 ? lines.indexOf(sameMaterial[0]) : -1;
};

/**
 * The JOINT order's offer-grouping derivations (Task 7) — split out of `JointBuyerBid.tsx` itself
 * so the container stays under the file's ~400-line budget. Every value here is derived from the
 * same two query results (`jointOrder`, `multipleOffersOnOrders`) via one `useMemo` chain apiece,
 * faithfully carried over from the pre-Carbon component body verbatim (same dependency arrays,
 * same intermediate names) — nothing about the derivation logic itself changed, only where it
 * lives.
 *
 * `cols` feeds `AwardJoint`: one entry per seller (`userId`), each holding one slot per order LINE,
 * so every seller's offers line up under the same line regardless of which lines they quoted. Keyed
 * by line, not material: a request can put the same material on several lines, and keying by
 * material collapsed those lines into one slot (one offer shown, and awarded, for all of them).
 */
export const useJointBidOffers = (
  jointOrder: OrderResponse[] | undefined,
  multipleOffersOnOrders: JointOfferResponse | undefined
) => {
  const multipleOffers = useMemo(() => {
    return multipleOffersOnOrders
      ? (Object.values(multipleOffersOnOrders) as [OfferResponse[]])
      : ([[]] as [OfferResponse[]]);
  }, [multipleOffersOnOrders]);

  const flattenedOffers = useMemo(
    () => flattenOffers(multipleOffers),
    [multipleOffers]
  );

  const cols = useMemo(() => {
    const columns: any = {};
    const lines = jointOrder ?? [];

    flattenedOffers.forEach((offer: any) => {
      const userId = offer.user.id;
      const index = lineIndexOf(lines, offer);
      if (index < 0) return;

      if (!columns[userId]) {
        columns[userId] = lines.map((line) => ({
          material: line.material?.id,
          materialName: line.material?.globalMaterialName,
          seller: null,
          offer: null,
        }));
      }

      columns[userId][index] = {
        material: offer.order.material.id,
        materialName: offer.order.material.globalMaterialName,
        seller: offer.user.company.name,
        offer,
      };
    });

    return columns;
  }, [flattenedOffers, jointOrder]);

  const selectedOffers = useMemo(() => {
    return flattenedOffers
      .filter((offer) => offer.selected)
      .sort((a, b) =>
        a.order.material.globalMaterialName.localeCompare(
          b.order.material.globalMaterialName
        )
      );
  }, [flattenedOffers]);

  const ordersWithoutOffers = useMemo(() => {
    if (!jointOrder) return undefined;
    const selectedOrdersIds = new Set(
      selectedOffers.map((offer) => offer.order.id)
    );
    return jointOrder.filter((order) => !selectedOrdersIds.has(order.id));
  }, [jointOrder, selectedOffers]);

  return {
    multipleOffers,
    flattenedOffers,
    cols,
    selectedOffers,
    ordersWithoutOffers,
  };
};
